// Per-chat sticky transcription language.
//
// Voice notes inside one chat are almost always in one language, and the
// short ones ("כן", "ok", "sí") are exactly where auto-detection is least
// reliable. So we remember what language transcriptions in a chat resolved
// to, and once the same language has been seen STICKY_MIN_HITS times we stop
// detecting and hand Whisper the remembered code directly — faster (skips
// the detect pass) and far more stable on short clips.
//
// Precedence (enforced by transcribeMessageThunk, not here):
//   explicit user pick in the Display Settings picker (non-auto)
//     > sticky chat language (hits >= STICKY_MIN_HITS)
//       > auto-detect
//
// Storage: localStorage 'bt:transcribe-chat-lang' in the content-script
// origin — deliberately NOT chrome.storage: only the thunk (content script)
// reads it, and the resolved language still travels to offscreen through
// the existing EXTRACT_DOC payload path. LRU-pruned to MAX_TRACKED_CHATS.
//
// This map serves BOTH consumers of the pipeline: the manual Transcribe
// buttons and future engine-dispatch callers (agent ingress) — both funnel
// through transcribeMessageThunk-equivalent orchestration with a chat-scoped
// message key.

const STORAGE_KEY = 'bt:transcribe-chat-lang';
const MAX_TRACKED_CHATS = 200;
/** How many consistent observations before the language "sticks". */
export const STICKY_MIN_HITS = 2;

interface ChatLangEntry {
  lang: string;      // normalised ISO-639-1 ('he', 'en', ...)
  hits: number;      // consecutive observations of this language
  updatedAt: number; // epoch ms — LRU key
}

type ChatLangMap = Record<string, ChatLangEntry>;

const normaliseLang = (raw: string): string =>
  raw.split('-')[0].split('_')[0].toLowerCase();

function load(): ChatLangMap {
  try {
    if (typeof localStorage === 'undefined') return {};
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as ChatLangMap) : {};
  } catch {
    return {};
  }
}

function save(map: ChatLangMap): void {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  } catch (e) {
    console.warn('[transcribe-chat-lang] persist failed', e);
  }
}

/** Extract the chat id (the JID) from a serialized WA message key.
 *  Two orders exist in the wild for the same key — the WAWeb DOM order
 *  `<fromMe>_<remoteJid>_<id>` and the legacy extension order
 *  `<remoteJid>_<fromMe>_<id>`. Rather than betting on one, find the
 *  segment that looks like a JID (contains '@'). Returns null when no
 *  segment qualifies. */
export function chatIdFromMessageKey(messageKey: string): string | null {
  if (!messageKey) return null;
  const parts = messageKey.split('_');
  for (const p of parts) {
    if (p.includes('@')) return p;
  }
  return null;
}

/** The remembered language for a chat, or null when nothing has stuck yet
 *  (fewer than STICKY_MIN_HITS consistent observations). */
export function getStickyChatLanguage(chatId: string | null): string | null {
  if (!chatId) return null;
  const entry = load()[chatId];
  if (!entry || entry.hits < STICKY_MIN_HITS) return null;
  return entry.lang;
}

/** Record the language a successful transcription resolved to. Consistent
 *  languages accumulate hits; a different language resets the entry to
 *  hits=1 (one odd detection shouldn't break stickiness — but repeated
 *  ones re-stick to the new language). Touches updatedAt for LRU. */
export function recordChatLanguage(chatId: string | null, lang: string | null | undefined): void {
  if (!chatId || !lang) return;
  const norm = normaliseLang(lang);
  if (!norm) return;
  const map = load();
  const existing = map[chatId];
  map[chatId] = existing && existing.lang === norm
    ? { lang: norm, hits: existing.hits + 1, updatedAt: Date.now() }
    : { lang: norm, hits: 1, updatedAt: Date.now() };

  // LRU prune — keep the most recently used MAX_TRACKED_CHATS entries.
  const keys = Object.keys(map);
  if (keys.length > MAX_TRACKED_CHATS) {
    keys
      .sort((a, b) => (map[b].updatedAt ?? 0) - (map[a].updatedAt ?? 0))
      .slice(MAX_TRACKED_CHATS)
      .forEach((k) => delete map[k]);
  }
  save(map);
  console.log(`[transcribe-chat-lang] ${chatId}: ${norm} (hits=${map[chatId].hits})`);
}
